import React, { useState } from 'react';
import { 
  Card, 
  Button, 
  Row, 
  Col, 
  Badge,
  Carousel,
  ListGroup,
  Form,
  Alert
} from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBed,
  faBath,
  faRuler,
  faHome,
  faCalendar,
  faMapMarkerAlt,
  faDollarSign,
  faHeart,
  faShare,
  faCalendarCheck,
  faPhone,
  faEnvelope,
  faChevronUp,
  faChevronDown,
  faStickyNote,
  faSave,
  faBrain,
  faCheckCircle,
  faCar,
  faExpand,
  faCompress,
  faEye,
  faBuilding
} from '@fortawesome/free-solid-svg-icons';

interface PropertyDetailsInlineProps {
  property: any;
  isExpanded: boolean;
  onToggleExpand: () => void;
  agentNotes?: string;
  onSaveNotes?: (notes: string) => void;
  onSaveProperty?: () => void;
  onScheduleTour?: () => void;
  leadName?: string;
  matchScore?: number;
  aiReasoning?: {
    primary?: string;
    factors?: string[];
  };
}

const PropertyDetailsInline: React.FC<PropertyDetailsInlineProps> = ({
  property,
  isExpanded,
  onToggleExpand,
  agentNotes = '',
  onSaveNotes,
  onSaveProperty,
  onScheduleTour,
  leadName = 'your client',
  matchScore,
  aiReasoning
}) => {
  const [localNotes, setLocalNotes] = useState(agentNotes);
  const [showNotesForm, setShowNotesForm] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [notesSaved, setNotesSaved] = useState(false);

  const handleSaveNotes = () => {
    if (onSaveNotes) {
      onSaveNotes(localNotes);
      setNotesSaved(true);
      setTimeout(() => setNotesSaved(false), 3000);
    }
  };

  const handleSaveProperty = () => {
    setIsSaved(true);
    if (onSaveProperty) {
      onSaveProperty();
    }
  };

  const formatPrice = (price: number) => {
    if (!price) return 'Price not available';
    return `$${price.toLocaleString()}`;
  };

  const getImages = () => {
    if (Array.isArray(property.images)) return property.images;
    if (property.images) return [property.images];
    return [];
  };

  const images = getImages();

  return (
    <Card className="property-details-inline mt-3 shadow-sm">
      <Card.Header className="bg-white border-0 p-3">
        <div className="d-flex justify-content-between align-items-center">
          <div>
            <h6 className="mb-1 fw-bold">
              {property.address || 'Property Details'}
            </h6>
            <small className="text-muted">
              {property.city}, {property.state || 'AZ'} {property.zip || property.postal_code}
              {property.mls_number && (
                <Badge bg="light" text="dark" className="ms-2">
                  MLS# {property.mls_number}
                </Badge>
              )}
            </small>
            {(property.listingBrokerage || property.listing_office_name) && (
              <div className="mt-1">
                <span className="text-muted" style={{ fontSize: '0.85rem', fontWeight: 500 }}>
                  <FontAwesomeIcon icon={faBuilding} className="me-1" />
                  Listing courtesy of {property.listingBrokerage || property.listing_office_name}
                  {(property.listing_agent_full_name || (typeof property.listingAgent === 'string' ? property.listingAgent : property.listingAgent?.name)) &&
                    ` - ${property.listing_agent_full_name || (typeof property.listingAgent === 'string' ? property.listingAgent : property.listingAgent?.name)}`}
                </span>
              </div>
            )}
          </div>
          <div className="d-flex gap-2">
            {matchScore && matchScore >= 90 && (
              <Badge bg="success">
                <FontAwesomeIcon icon={faCheckCircle} className="me-1" />
                Perfect Match
              </Badge>
            )}
            <Button
              variant={isExpanded ? "primary" : "outline-primary"}
              size="sm"
              onClick={onToggleExpand}
            >
              <FontAwesomeIcon icon={isExpanded ? faCompress : faExpand} className="me-1" />
              {isExpanded ? 'Less' : 'More'} Details
            </Button>
          </div>
        </div>
      </Card.Header>

      {isExpanded && (
        <Card.Body className="p-0">
          <Row className="g-0">
            {/* Left Column - Images & Main Info */}
            <Col lg={8} className="border-end">
              {/* Image Carousel */}
              {images.length > 0 && (
                <div className="position-relative" style={{ maxHeight: '400px' }}>
                  <Carousel interval={null}>
                    {images.map((image, idx) => (
                      <Carousel.Item key={idx}>
                        <img
                          className="d-block w-100"
                          src={image}
                          alt={`Property view ${idx + 1}`}
                          style={{ 
                            height: '400px', 
                            objectFit: 'cover' 
                          }}
                          onError={(e) => {
                            e.currentTarget.src = 'https://via.placeholder.com/800x600/1f2937/ffffff?text=No+Image';
                          }}
                        />
                      </Carousel.Item>
                    ))}
                  </Carousel>
                  
                  {/* Quick Actions Overlay */}
                  <div className="position-absolute top-0 end-0 p-2">
                    <div className="d-flex gap-2">
                      <Button
                        variant={isSaved ? "success" : "light"}
                        size="sm"
                        onClick={handleSaveProperty}
                        className="shadow-sm"
                      >
                        <FontAwesomeIcon icon={faHeart} className={isSaved ? "" : "text-muted"} />
                      </Button>
                      <Button variant="light" size="sm" className="shadow-sm">
                        <FontAwesomeIcon icon={faShare} className="text-muted" />
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              <div className="p-3">
                {/* Price & Key Stats */}
                <div className="mb-4">
                  <div className="d-flex justify-content-between align-items-start mb-2">
                    <div>
                      <h3 className="text-primary mb-1">{formatPrice(property.price)}</h3>
                      <p className="text-muted mb-0">
                        ${property.price && property.square_feet ? 
                          Math.round(property.price / property.square_feet) : 0}/sqft
                      </p>
                    </div>
                    <Badge bg="success" className="ms-2">For Sale</Badge>
                  </div>

                  {/* Property Stats Grid */}
                  <Row className="g-3 text-center">
                    <Col xs={3}>
                      <div className="p-2 bg-light rounded">
                        <FontAwesomeIcon icon={faBed} className="text-muted mb-1" />
                        <div className="fw-bold">{property.bedrooms || property.beds || 0}</div>
                        <small className="text-muted">Beds</small>
                      </div>
                    </Col>
                    <Col xs={3}>
                      <div className="p-2 bg-light rounded">
                        <FontAwesomeIcon icon={faBath} className="text-muted mb-1" />
                        <div className="fw-bold">{property.bathrooms || property.baths || 0}</div>
                        <small className="text-muted">Baths</small>
                      </div>
                    </Col>
                    <Col xs={3}>
                      <div className="p-2 bg-light rounded">
                        <FontAwesomeIcon icon={faRuler} className="text-muted mb-1" />
                        <div className="fw-bold">
                          {property.square_feet?.toLocaleString() || property.sqft?.toLocaleString() || '0'}
                        </div>
                        <small className="text-muted">Sq Ft</small>
                      </div>
                    </Col>
                    <Col xs={3}>
                      <div className="p-2 bg-light rounded">
                        <FontAwesomeIcon icon={faHome} className="text-muted mb-1" />
                        <div className="fw-bold">{property.year_built || 'N/A'}</div>
                        <small className="text-muted">Built</small>
                      </div>
                    </Col>
                  </Row>
                </div>

                {/* AI Analysis */}
                {aiReasoning && (
                  <Card className="mb-3 border-0 bg-light">
                    <Card.Body>
                      <div className="d-flex align-items-center mb-2">
                        <FontAwesomeIcon icon={faBrain} className="text-primary me-2" />
                        <strong className="text-primary">AI Analysis</strong>
                        {matchScore && (
                          <Badge bg="primary" className="ms-auto">
                            {matchScore}% Match
                          </Badge>
                        )}
                      </div>
                      {aiReasoning.primary && (
                        <p className="mb-2 text-muted small">
                          {aiReasoning.primary}
                        </p>
                      )}
                      {aiReasoning.factors && aiReasoning.factors.length > 0 && (
                        <ul className="mb-0 small list-unstyled">
                          {aiReasoning.factors.map((factor, idx) => (
                            <li key={idx} className="mb-1">
                              <FontAwesomeIcon 
                                icon={faCheckCircle} 
                                className="text-success me-2" 
                                size="sm"
                              />
                              {factor}
                            </li>
                          ))}
                        </ul>
                      )}
                    </Card.Body>
                  </Card>
                )}

                {/* Property Description */}
                {property.description && (
                  <div className="mb-4">
                    <h6 className="mb-2">Property Description</h6>
                    <p className="text-muted small">{property.description}</p>
                  </div>
                )}

                {/* Property Details */}
                <div>
                  <h6 className="mb-3">Property Details</h6>
                  <ListGroup variant="flush" className="mb-3">
                    <ListGroup.Item className="d-flex justify-content-between px-0 py-2">
                      <span className="text-muted">Property Type</span>
                      <span>{property.property_type || 'Single Family'}</span>
                    </ListGroup.Item>
                    {property.days_on_market !== undefined && (
                      <ListGroup.Item className="d-flex justify-content-between px-0 py-2">
                        <span className="text-muted">Days on Market</span>
                        <span>
                          {property.days_on_market}
                          {property.days_on_market <= 7 && (
                            <Badge bg="danger" className="ms-2">NEW</Badge>
                          )}
                        </span>
                      </ListGroup.Item>
                    )}
                    {property.garage && (
                      <ListGroup.Item className="d-flex justify-content-between px-0 py-2">
                        <span className="text-muted">Garage</span>
                        <span>
                          <FontAwesomeIcon icon={faCar} className="me-1" />
                          {property.garage} Car
                        </span>
                      </ListGroup.Item>
                    )}
                    {property.lot_size && (
                      <ListGroup.Item className="d-flex justify-content-between px-0 py-2">
                        <span className="text-muted">Lot Size</span>
                        <span>
                          {property.lot_size.toLocaleString()} sqft
                          {property.lot_size >= 43560 && (
                            <span className="text-muted ms-1">
                              ({(property.lot_size / 43560).toFixed(2)} acres)
                            </span>
                          )}
                        </span>
                      </ListGroup.Item>
                    )}
                  </ListGroup>
                </div>
              </div>
            </Col>

            {/* Right Column - Actions & Agent Notes */}
            <Col lg={4}>
              <div className="p-3">
                {/* Action Buttons */}
                <div className="mb-4">
                  <Button
                    variant="primary"
                    size="lg"
                    className="w-100 mb-2"
                    onClick={() => setShowNotesForm(true)}
                  >
                    <FontAwesomeIcon icon={faCalendarCheck} className="me-2" />
                    {localNotes ? 'Edit Agent Notes' : 'Add Agent Notes'}
                  </Button>
                  <Row className="g-2">
                    <Col xs={6}>
                      <Button variant="outline-primary" className="w-100">
                        <FontAwesomeIcon icon={faPhone} className="me-2" />
                        Call Agent
                      </Button>
                    </Col>
                    <Col xs={6}>
                      <Button variant="outline-primary" className="w-100">
                        <FontAwesomeIcon icon={faEnvelope} className="me-2" />
                        Message
                      </Button>
                    </Col>
                  </Row>
                </div>

                {/* Agent Notes Section */}
                <Card className="border-0 bg-light">
                  <Card.Body>
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <h6 className="mb-0">
                        <FontAwesomeIcon icon={faStickyNote} className="me-2 text-primary" />
                        Agent Notes
                      </h6>
                      <Button
                        variant="link"
                        size="sm"
                        onClick={() => setShowNotesForm(!showNotesForm)}
                        className="text-primary p-0"
                      >
                        {showNotesForm ? 'Cancel' : localNotes ? 'Edit' : 'Add Note'}
                      </Button>
                    </div>

                    {showNotesForm ? (
                      <Form>
                        <Form.Group>
                          <Form.Control
                            as="textarea"
                            rows={4}
                            value={localNotes}
                            onChange={(e) => setLocalNotes(e.target.value)}
                            placeholder={`Why is this property perfect for ${leadName}? Add your personalized insights here...`}
                            className="mb-2"
                          />
                          <Form.Text className="text-muted">
                            Explain why you think this property matches {leadName}'s needs
                          </Form.Text>
                        </Form.Group>
                        <div className="d-flex gap-2 mt-3">
                          <Button
                            variant="primary"
                            size="sm"
                            onClick={() => {
                              handleSaveNotes();
                              setShowNotesForm(false);
                            }}
                          >
                            <FontAwesomeIcon icon={faSave} className="me-1" />
                            Save Note
                          </Button>
                          <Button
                            variant="outline-secondary"
                            size="sm"
                            onClick={() => {
                              setLocalNotes(agentNotes);
                              setShowNotesForm(false);
                            }}
                          >
                            Cancel
                          </Button>
                        </div>
                      </Form>
                    ) : (
                      <>
                        {localNotes ? (
                          <div className="agent-note-display">
                            <p className="mb-0 small">{localNotes}</p>
                          </div>
                        ) : (
                          <p className="text-muted small mb-0">
                            No agent notes yet. Add your insights about why this property 
                            is a good match for {leadName}.
                          </p>
                        )}
                      </>
                    )}

                    {notesSaved && (
                      <Alert variant="success" className="mt-2 mb-0 py-1 px-2 small">
                        <FontAwesomeIcon icon={faCheckCircle} className="me-1" />
                        Note saved successfully!
                      </Alert>
                    )}
                  </Card.Body>
                </Card>

                {/* Features */}
                {property.features && property.features.length > 0 && (
                  <div className="mt-3">
                    <h6 className="mb-2">Features</h6>
                    <div className="d-flex flex-wrap gap-2">
                      {property.features.map((feature: string, idx: number) => (
                        <Badge key={idx} bg="secondary" className="py-2 px-3">
                          {feature}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                {/* Market Insights */}
                <Card className="mt-3 border-0 bg-light">
                  <Card.Body className="small">
                    <h6 className="mb-2">Market Insights</h6>
                    <p className="mb-1 text-muted">
                      This property is priced competitively compared to
                      similar {property.bedrooms || property.beds}-bedroom homes in {property.city}.
                    </p>
                    <p className="mb-0 text-muted">
                      Based on historical data, properties in this area have seen 
                      an average appreciation of 8-12% annually.
                    </p>
                  </Card.Body>
                </Card>
                
                {/* MLS Attribution */}
                <Card className="mt-3 border-0 bg-light">
                  <Card.Body className="small">
                    <div style={{ color: '#6c757d' }}>
                      <p className="mb-1" style={{ fontSize: '0.85rem', fontWeight: 500 }}>
                        <FontAwesomeIcon icon={faBuilding} className="me-1" />
                        Listing courtesy of{' '}
                        {property.listingBrokerage || property.listing_office_name || (typeof property.listingAgent === 'object' ? property.listingAgent?.officeName : '') || 'MLS Partner'}
                        {(property.listing_agent_full_name || (typeof property.listingAgent === 'object' ? property.listingAgent?.name : typeof property.listingAgent === 'string' ? property.listingAgent : '')) && (
                          <span> - {property.listing_agent_full_name || (typeof property.listingAgent === 'object' ? property.listingAgent?.name : property.listingAgent)}</span>
                        )}
                      </p>
                      <p className="mb-1" style={{ fontSize: '0.7rem', fontStyle: 'italic' }}>
                        The properties featured in this analysis are not representative of the displaying agent's or brokerage's transaction history and are provided for comparative market analysis purposes only.
                      </p>
                      <hr className="my-2" />
                      <p className="mb-0" style={{ fontSize: '0.7rem' }}>
                        The data relating to real estate for sale on this website comes in part from the
                        Internet Data Exchange (IDX) Program of Arizona Regional Multiple Listing Service, Inc.
                        © {new Date().getFullYear()} Arizona Regional MLS. All rights reserved.
                      </p>
                    </div>
                  </Card.Body>
                </Card>
              </div>
            </Col>
          </Row>
        </Card.Body>
      )}
    </Card>
  );
};

export default PropertyDetailsInline;